import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { object, string, date, number } from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Clock
} from 'lucide-react';
import Regex from "../../utils/constants/Regex";
import { yupResolver } from "@hookform/resolvers/yup";
import { createAppointment } from "./AppointmentServices";
import { useUsers } from "../provider/UserProvider";
import {
  formatDateTimeForBackend,
  formatDateTimeForInput,
  isDateInTheFuture,
  isTimeBetweenBoundaries,
  timeOptions,
} from "../../utils/functions/Helpers";
import { ToastContainer } from "react-toastify";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import Constants from "../../utils/constants/Constants";
import { AppointmentShape } from "../../utils/models/AppointmentModel";
import { tokens } from "../../theme";

const schema = object().shape({
  id: number().nullable().optional(),
  firstName: string().required("First name is required"),
  lastName: string().required("Last name is required"),
  email: string().email().required("Email is required"),
  phone: string()
    .required("Phone is required")
    .matches(Regex.PHONE_REGEX, "Invalid phone number"),
  dateTime: date()
    .transform((value, originalValue) => {
      return originalValue ? new Date(originalValue) : null;
    })
    .test("is-between", "Time must be between 9:00 AM and 7:30 PM", (value) =>
      isTimeBetweenBoundaries(value)
    )
    .test("is-in-the-future", "Date and time must be in the future", (value) =>
      isDateInTheFuture(value)
    )
    .required("Date and time are required"),
  raison: string().required("The raison is required"),
  status: string().required("The status is required"),
  additionalInfo: string().nullable().optional(),
  userId: string().nullable().optional(),
});

const buildAppointmentFormValues = (appointment) => ({
  id: appointment?.id ?? null,
  firstName: appointment?.firstName ?? "",
  lastName: appointment?.lastName ?? "",
  email: appointment?.email ?? "",
  phone: appointment?.phone ?? "",
  dateTime: appointment?.dateTime
    ? formatDateTimeForInput(appointment.dateTime)
    : "",
  raison: appointment?.raison ?? "",
  status: appointment?.status ?? Constants.STATUS.Pending,
  additionalInfo: appointment?.additionalInfo ?? "",
  userId: appointment?.userId ?? "",
});

const AppointmentForm = (props) => {
  const {
    appointment,
    updateAppointment,
    toggleModal,
    setState,
    isUpdating = false,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const setApiError = () => {};
  const { usersData, setParams } = useUsers();
  const setIsLoading = useLoadingSpinner();

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: buildAppointmentFormValues(appointment),
  });

  const now = new Date().toLocaleString("en-US", timeOptions);
  const todayAt12AM = new Date(now).setHours(0, 0, 0, 0);
  const nextDayAt12AM = new Date(todayAt12AM).setHours(20, 0, 0, 0);

  const minDateTime =
    new Date(now) > new Date(now).setHours(19, 30, 0, 0)
      ? new Date(nextDayAt12AM).toISOString().slice(0, 16)
      : new Date(todayAt12AM).toISOString().slice(0, 16);

  const setFalsyValuesToNull = (data) => {
    Object.keys(data).forEach((key) => {
      if (!data[key]) {
        data[key] = null;
      }
    });
    return data;
  };

  const onSubmit = async (data) => {
    data.dateTime = formatDateTimeForBackend(data.dateTime);
    data = setFalsyValuesToNull(data);

    setIsLoading(true);

    try {
      if (isUpdating) {
        await updateAppointment(data, setApiError, setState, setIsLoading);
      } else {
        data.userId =
          usersData?.content?.find(
            (user) =>
              user.email === data.email &&
              user.firstName === data.firstName &&
              user.lastName === data.lastName
          )?.userId ?? null;
        await createAppointment(data, setApiError, setState, setIsLoading);
      }

      reset();
      toggleModal(false);
    } catch {
      // Error feedback is handled in the service layer.
    }
  };

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal(false);
  };

  useEffect(() => {
    setParams({ pageSize: "All", page: 0 });
  }, [setParams]);

  useEffect(() => {
    reset(buildAppointmentFormValues(appointment));
  }, [appointment, reset]);

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Calendar size={28} color={colors.greenAccent[500]} />
              <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
                {isUpdating ? "Refine Rendezvous" : "Schedule Consultation"}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 2 }}>
              Organize a visit or consultation for prospective guests.
            </Typography>
          </Grid>

          {/* Personal Details */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <User size={18} /> Client Liaison
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  {...register("firstName")}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  {...register("lastName")}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Contact"
                  {...register("phone")}
                  placeholder="1 (614) 316-1430"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Appointment Details */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={18} /> Appointment Particulars
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Preferred Schedule"
                  type="datetime-local"
                  {...register("dateTime")}
                  min={minDateTime}
                  error={!!errors.dateTime}
                  helperText={errors.dateTime?.message}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primal Reason"
                  {...register("raison")}
                  error={!!errors.raison}
                  helperText={errors.raison?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.status}>
                  <InputLabel id="status-label">Operational Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select
                        labelId="status-label"
                        label="Operational Status"
                        {...field}
                        sx={{ borderRadius: "12px" }}
                      >
                        <MenuItem value="">Select status</MenuItem>
                        {Object.keys(Constants.STATUS).map((key) => (
                          <MenuItem key={key} value={Constants.STATUS[key]}>
                            {Constants.STATUS[key]}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.status?.message}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Supplemental Intelligence"
                  {...register("additionalInfo")}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isUpdating ? 'Secure Updates' : 'Confirm Appointment'}
            </Button>
          </Grid>
        </Grid>
      </form>
      <ToastContainer />
    </Box>
  );
};

AppointmentForm.propTypes = {
  appointment: PropTypes.shape(AppointmentShape),
  updateAppointment: PropTypes.func,
  isUpdating: PropTypes.bool,
  toggleModal: PropTypes.func.isRequired,
  setState: PropTypes.func,
};

export default AppointmentForm;
